import axios from "axios";
import apiV2 from "api-v2";
import type {
	SupplierEvaluationSummary,
	SupplierScoringDetail,
	SaveCriterionScorePayload,
	ScoringInstruction,
	CompletionStatus,
	CriterionScore,
} from "../types/scoring.type";

const unwrap = <T>(responseData: unknown): T => {
	if (
		responseData &&
		typeof responseData === "object" &&
		"data" in responseData
	) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export type MemberFinalStatus = "pending" | "in_progress" | "submitted";

export interface SupplierEvaluationsResponse {
	memberFinalStatus: MemberFinalStatus;
	memberFinalSubmittedAt: string | null;
	notPermitted: boolean;
	readOnly: boolean;
	submittedAt: string | null;
	suppliers: SupplierEvaluationSummary[];
}

export const getSupplierEvaluations = async (
	rfxId: number
): Promise<SupplierEvaluationsResponse> => {
	try {
		// A blocked panel member (revoked, or a declared conflict awaiting RFx
		// Lead review) gets a 403 here. That state is handled in the UI, so
		// suppress the global error toast for it.
		const response = await apiV2.get(`/rfx/${rfxId}/evaluation/submissions`, {
			expectedErrors: [403],
		} as Record<string, unknown>);
		const data = unwrap<Record<string, unknown>>(response.data);

		const rawSuppliers = (data.suppliers as Record<string, unknown>[]) ?? [];
		const readOnly = (data.read_only as boolean) ?? false;
		const notPermitted = (data.not_permitted as boolean) ?? false;
		const memberFinalStatus =
			(data.final_status as MemberFinalStatus) ?? "pending";
		const submittedAt = (data.submitted_at as string) ?? null;
		const memberFinalSubmittedAt = (data.final_submitted_at as string) ?? null;

		return {
			suppliers: rawSuppliers.map((item) => ({
				inviteId: item.invite_id as number,
				supplierName: item.supplier_name as string,
				supplierLogo: item.supplier_logo as string,
				companyId: item.company_id as number,
				status: item.status as SupplierEvaluationSummary["status"],
				percentComplete: item.percent_complete as number,
				startedAt: (item.started_at as string) ?? null,
				completedAt: (item.completed_at as string) ?? null,
				finalStatus:
					(item.final_status as SupplierEvaluationSummary["status"]) ?? null,
				finalPercentComplete: (item.final_percent_complete as number) ?? null,
				finalSubmittedAt: (item.final_submitted_at as string) ?? null,
			})),
			readOnly,
			notPermitted,
			submittedAt,
			memberFinalStatus,
			memberFinalSubmittedAt,
		} as SupplierEvaluationsResponse;
	} catch (err) {
		if (axios.isAxiosError(err) && err.response?.status === 403) {
			return {
				suppliers: [],
				readOnly: true,
				notPermitted: true,
				submittedAt: null,
				memberFinalStatus: "pending" as const,
				memberFinalSubmittedAt: null,
			};
		}
		throw err;
	}
};

export const getSupplierScores = async (
	rfxId: number,
	inviteId: number
): Promise<SupplierScoringDetail> => {
	const response = await apiV2.get(
		`/rfx/${rfxId}/evaluation/scores/${inviteId}`
	);
	const raw = unwrap<Record<string, unknown>>(response.data);
	const rawScores = (raw.scores as Record<string, unknown>[]) ?? [];
	return {
		inviteId: raw.invite_id as number,
		supplierName: raw.supplier_name as string,
		scores: rawScores.map((s) => ({
			id: (s.id as number | null) ?? null,
			rfxCriteriaId: s.rfx_criteria_id as number,
			criterionName: s.criterion_name as string,
			criterionDescription: (s.criterion_description as string | null) ?? null,
			criterionWeight: s.criterion_weight as number,
			score: (s.score as number | null) ?? null,
			comment: (s.comment as string | null) ?? null,
			updatedAt: (s.updated_at as string | null) ?? null,
		})),
	};
};

export const saveCriterionScore = async (
	rfxId: number,
	inviteId: number,
	payload: SaveCriterionScorePayload
): Promise<CriterionScore> => {
	const response = await apiV2.put(
		`/rfx/${rfxId}/evaluation/scores/${inviteId}`,
		payload
	);
	return unwrap<CriterionScore>(response.data);
};

export const getScoringInstructions = async (
	rfxId: number
): Promise<ScoringInstruction> => {
	const response = await apiV2.get(
		`/rfx/${rfxId}/evaluation/scoring-instructions`
	);
	return unwrap<ScoringInstruction>(response.data);
};

export const getCompletionStatus = async (
	rfxId: number
): Promise<CompletionStatus> => {
	const response = await apiV2.get(
		`/rfx/${rfxId}/evaluation/scores/completion`
	);
	const raw = unwrap<Record<string, unknown>>(response.data);
	return {
		isComplete: !!raw.is_complete,
		summary: {
			totalSuppliers:
				(raw.summary as Record<string, number>)?.total_suppliers ?? 0,
			fullyScored: (raw.summary as Record<string, number>)?.fully_scored ?? 0,
			totalCriteria:
				(raw.summary as Record<string, number>)?.total_criteria ?? 0,
		},
	};
};
